import React, { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LoadResponse } from "../../../../models/api/load";
import { CustomDate } from "../../../../models/custom-date/custom-date";
import { EmployeeGroupDTO } from "../../../../models/employee-groups";
import { TnATimesheetConfirmationsDTO } from "../../../../models/tna-timesheet-confirmation";
import { exportTnAToExcel } from "../../../../utils/export-presence-and-leaves";
import { getNormaleDateOrMonth, intlFormatDateShort, intlFormatDateWithHour } from "../../../../utils/time-utils";
import { monthsValues } from "../../../CustomDropdowns/CustomDropdownMonthsValues/CustomDropdownMonthsValues";
import ExportToExcelDialog from "../../ExportToExcelDialog/ExportToExcelDialog";
import { MyTnAContext } from "../../MyTeamPresenceAndLeaves";
import { excelStyles } from "../../utils";

interface Props {
  disabled: boolean;
  loadEmployeeGroup: () => Promise<EmployeeGroupDTO | undefined>;
  loadTnATimesheetConfirmationData: (
    pageSize?: number
  ) => Promise<LoadResponse<TnATimesheetConfirmationsDTO> | undefined>;
  allItems: () => TnATimesheetConfirmationsDTO[];
}

const ExportTimesheetConfirmationsToExcel = ({
  disabled,
  loadEmployeeGroup,
  loadTnATimesheetConfirmationData,
  allItems,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.team_presence_and_leaves_time_sheet_confirmation";
  const translationFields = `${tRP}.fields`;
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";

  const {
    legalEntity: { legalEntityOption, selectedLegalEntity },
    employeeGroup: { employeeGroupOption },
    currentEmployees: { onlyCurrentEmployeesOptionValue },
    searchingDate: { year, month },
  } = useContext(MyTnAContext);

  const rowsBeforeHeaderInExcel = useCallback(
    (employeeGroup: EmployeeGroupDTO | undefined) => {
      return [
        {
          data: ["Timesheet confirmation"],
          fontSize: 16,
          isBold: true,
        },
        {
          data: [`Report date ${intlFormatDateWithHour(new Date())}`],
          fontSize: 14,
          isBold: false,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `Legal entities: ${!legalEntityOption?.legalEntityIds?.length ? "All" : selectedLegalEntity?.name ?? ""}`,
          ],
          fontSize: 16,
          isBold: true,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `Employee group: ${
              !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
            }`,
          ],
          fontSize: 12,
          isBold: false,
        },
        {
          data: [
            `Employee status: ${
              onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees ? "Only current employees" : "All employees"
            }`,
          ],
          fontSize: 12,
          isBold: false,
          emptyRowsUnder: 1,
        },
        {
          data: [`Period: ${year} ${t(monthsValues.find((currMonth) => currMonth.value === month)?.key ?? "")}`],
          fontSize: 14,
          isBold: true,
        },
      ];
    },
    [
      legalEntityOption?.legalEntityIds,
      selectedLegalEntity?.name,
      employeeGroupOption,
      onlyCurrentEmployeesOptionValue,
      year,
      month,
    ]
  );

  const headerNames = useMemo(() => {
    return [
      `${t(`${tRPCommon}.employee_code`)}`,
      `${t(`${tRPCommon}.internal_id`)}`,
      `${t("common_words.employee")}`,
      `${t(`${tRPCommon}.birth_date`)}`,
      `${t("common_words.legal_entity")}`,
      `${t("common_words.tna_employee_group")}`,
      `${t(`${translationFields}.asOf_date`)}`,
      `${t(`${translationFields}.signedAsOf_date`)}`,
    ];
  }, []);

  const dataToExportAsExcel = useCallback(
    (allItems: TnATimesheetConfirmationsDTO[]) => {
      return allItems.map((item) => {
        return {
          [`${t(`${tRPCommon}.employee_code`)}`]: { value: item.employee?.employeeCode ?? " " },
          [`${t(`${tRPCommon}.internal_id`)}`]: { value: item.employee?.internalId ?? " " },
          [`${t("common_words.employee")}`]: {
            value: item.employee?.name ?? " ",
            style: { ...excelStyles.employeeCellStyle },
          },
          [`${t(`${tRPCommon}.birth_date`)}`]: {
            value: item.employee?.birthDate ? intlFormatDateShort(item.employee?.birthDate) : " ",
          },
          [`${t("common_words.legal_entity")}`]: { value: item.employee?.legalEntity?.name ?? " " },
          [`${t("common_words.tna_employee_group")}`]: { value: item?.employee?.tnAGroup?.name ?? " " },
          [`${t(`${translationFields}.asOf_date`)}`]: {
            value: item.asOfDate ? intlFormatDateShort(CustomDate.ToDate(item.asOfDate) ?? new Date()) : " ",
          },
          [`${t(`${translationFields}.signedAsOf_date`)}`]: {
            value: item.signedAsOfDate
              ? intlFormatDateShort(CustomDate.ToDate(item.signedAsOfDate) ?? new Date())
              : " ",
          },
          sortDirection: headerNames,
        };
      });
    },
    [headerNames]
  );

  const exportToExcel = useCallback(
    async (option: "exportAllData" | "exportCurrent" = "exportCurrent") => {
      if (option === "exportCurrent") {
        const employeeGroup = employeeGroupOption?.employeeGroupIds?.length ? await loadEmployeeGroup() : undefined;

        exportTnAToExcel(
          dataToExportAsExcel(allItems()),
          headerNames as string[],
          `${new Date().getFullYear()} ${getNormaleDateOrMonth(new Date().getMonth() + 1)} ${getNormaleDateOrMonth(
            new Date().getDate()
          )} ${!legalEntityOption?.legalEntityIds?.length ? "All legal entities" : selectedLegalEntity?.name ?? ""} ${
            !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
          } Timesheet confirmation`,
          rowsBeforeHeaderInExcel(employeeGroup)
        );
      }

      if (option === "exportAllData") {
        const employeeGroup = employeeGroupOption?.employeeGroupIds?.length ? await loadEmployeeGroup() : undefined;

        const firstResponse = await loadTnATimesheetConfirmationData();

        if (firstResponse?.error) return;

        const allTnATimesheetConfirmationData = await loadTnATimesheetConfirmationData(firstResponse?.totalItems);

        if (!!allTnATimesheetConfirmationData?.error || !allTnATimesheetConfirmationData?.items) return;

        exportTnAToExcel(
          dataToExportAsExcel(allTnATimesheetConfirmationData.items),
          headerNames as string[],
          `${new Date().getFullYear()} ${getNormaleDateOrMonth(new Date().getMonth() + 1)} ${getNormaleDateOrMonth(
            new Date().getDate()
          )} ${!legalEntityOption?.legalEntityIds?.length ? "All legal entities" : selectedLegalEntity?.name ?? ""} ${
            !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
          } Timesheet confirmation`,
          rowsBeforeHeaderInExcel(employeeGroup)
        );
      }
    },
    [
      rowsBeforeHeaderInExcel,
      loadEmployeeGroup,
      headerNames,
      dataToExportAsExcel,
      employeeGroupOption?.employeeGroupIds,
      loadTnATimesheetConfirmationData,
    ]
  );

  return (
    <ExportToExcelDialog
      handleExportToExcel={exportToExcel}
      disabled={disabled}
    />
  );
};

export default ExportTimesheetConfirmationsToExcel;
